import React, { ReactElement } from 'react';

import { KMLEntry } from '../../../interfaces';
import { days, daysNamed } from './daysDefinition';
import { Enforce } from './Enforce';
import { getEnforceStatus } from './utils';
import { haversineFormula } from '../../routeEditor/mergeMetadataFromPoints';
import { isEUprod } from '../../../utils';

export function MarkerDescription(props: { marker: KMLEntry }) {
  const { marker } = props;

  const { block_name, route_direction, bus_stop_id, event_types } = marker;

  const enforce = getEnforceStatus(marker.enforcement_info[0]);
  const elems: ReactElement[] = [];
  const isEu = isEUprod();

  // Calculate geometry length if event_types contains BUS_STOP
  const calculateGeometryLength = (): number => {
    if (!marker.geometry?.coordinates || !Array.isArray(marker.geometry.coordinates)) {
      return 0;
    }

    let totalLength = 0;
    const { coordinates } = marker.geometry;

    for (let i = 0; i < coordinates.length - 1; i++) {
      const [lon1, lat1] = coordinates[i];
      const [lon2, lat2] = coordinates[i + 1];
      totalLength += haversineFormula(lat1, lon1, lat2, lon2);
    }

    return totalLength;
  };

  // Add geometry length for BUS_STOP event types (internal only)
  if (event_types && event_types.includes('BUS_STOP')) {
    const lengthInMeters = calculateGeometryLength();
    if (lengthInMeters > 0) {
      const unit = isEu ? 'meters' : 'feet';
      const localizedLength = isEu ? lengthInMeters : lengthInMeters * 3.28084; // Convert to feet
      elems.push(
        <>
          Segment length: {localizedLength.toFixed(0)} {unit}
        </>,
        <br />,
      );
    }
  }

  if (!enforce) {
    elems.push(<>Enforce rules not set</>, <br />);
  } else if (enforce === Enforce.hours) {
    elems.push(<>Enforce between: </>);
    if (marker.enforcement_info.length > 1) {
      elems.push(<br />);
    }

    marker.enforcement_info.forEach((e) => {
      const { enforce_start, enforce_end, enforce_days } = e;
      elems.push(
        <strong>
          {toTwelveHrFormat(enforce_start)} - {toTwelveHrFormat(enforce_end)}
        </strong>,
      );

      const daysDesc: string[] = [];
      const intervals = [];
      let idx = 0;

      for (let i = 0; i < days.length; i++) {
        if (enforce_days?.includes(i)) {
          if (!intervals[idx]) {
            intervals[idx] = [];
          }

          intervals[idx].push(i);
        } else {
          idx++;
        }
        // looking for broken intervals
      }

      intervals.forEach((i) => {
        const start = Math.min(...i);
        const end = Math.max(...i);

        if (start === end) {
          daysDesc.push(`${daysNamed[start]}`);
        } else {
          daysDesc.push(`${daysNamed[start]} - ${daysNamed[end]}`);
        }
      });

      elems.push(<>{` ${daysDesc.join(', ')}`}</>);
      elems.push(<br />);
    });
  } else if (enforce === Enforce.never) {
    elems.push(<>Enforce rules not active</>, <br />);
  } else if (enforce === Enforce.always) {
    elems.push(<>Always enforce</>, <br />);
  }

  if (bus_stop_id) {
    elems.push(<>{`Bus stop: ${bus_stop_id}`}</>);
  } else if (block_name) {
    elems.push(<>{[route_direction, block_name].join(': ')}</>, <br />);
  }

  return elems;
}

const formatTime = (time: number): string => {
  const hours = Math.floor(time / 1);
  const decimals = time % 1;
  const minutes = Math.floor(decimals * 60);

  const ampm = hours >= 12 ? 'pm' : 'am';

  if (minutes === 0) {
    return `${hours} ${ampm}`;
  }

  return `${hours}:${minutes} ${ampm}`;
};

const toTwelveHrFormat = (time: number): string => {
  if (time == 12) {
    return '12 pm';
  }
  if (time == 24 || time == 0) {
    return '12 am';
  }

  return formatTime(time);
};
